import { parseISO } from "date-fns";

export const unconfirmedBookingMessage =
  "Bokningen kunde inte bekräftas. Kontrollera dina bokningar innan du försöker igen.";

function validBookingId(value) {
  return (
    (typeof value === "string" && value.trim().length > 0) ||
    (Number.isSafeInteger(value) && value > 0)
  );
}

function bookingsURL(apiPath, apartmentId) {
  return `/api${apiPath}/apartments/${encodeURIComponent(
    apartmentId,
  )}/bookings`;
}

export async function createVerifiedBooking({
  apiPath,
  apartmentId,
  startTime,
  length,
  groupId,
  groupName,
}) {
  const url = bookingsURL(apiPath, apartmentId);
  let response;
  let result;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ startTime, length, groupId }),
      cache: "no-store",
    });
    result = await response.json();
  } catch {
    // A lost response does not mean Aptus failed to create the booking.
    throw new Error(unconfirmedBookingMessage);
  }

  if (!response.ok) {
    throw new Error(result?.message || unconfirmedBookingMessage);
  }

  const bookingId = result?.data?.bookingId;
  if (!validBookingId(bookingId)) {
    throw new Error(unconfirmedBookingMessage);
  }

  try {
    // Read back the exact booking without the hook's or browser's cached list.
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(unconfirmedBookingMessage);
    }
    const { data: bookings } = await response.json();
    const booking = Array.isArray(bookings)
      ? bookings.find((item) => String(item?.bookingId) === String(bookingId))
      : null;
    const expectedStart = parseISO(startTime).getTime();
    const actualStart = parseISO(booking?.startTime || "").getTime();
    const actualStop = parseISO(booking?.stopTime || "").getTime();
    const sameGroup =
      booking?.groupId != null
        ? String(booking.groupId) === String(groupId)
        : booking?.groupName === groupName;

    if (
      !booking ||
      typeof booking.groupName !== "string" ||
      !booking.groupName.trim() ||
      !sameGroup ||
      actualStart !== expectedStart ||
      actualStop - actualStart !== length * 60_000 ||
      booking.isReleased ||
      booking.isUsed ||
      booking.isExpired
    ) {
      throw new Error(unconfirmedBookingMessage);
    }

    return { booking, bookings };
  } catch {
    // Never repeat POST when verification fails: it could create a duplicate.
    throw new Error(unconfirmedBookingMessage);
  }
}

export async function cancelVerifiedBooking({
  apiPath,
  apartmentId,
  bookingId,
}) {
  if (!apartmentId || !validBookingId(bookingId)) {
    throw new Error("Bokningen saknar ett giltigt boknings-ID.");
  }
  const response = await fetch(bookingsURL(apiPath, apartmentId), {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bookingId }),
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(
      "Avbokningen kunde inte bekräftas. Kontrollera dina bokningar innan du försöker igen.",
    );
  }
}
